import { BaseClient, normalizeQuery, type Paged } from '../base-client';
import type { ExportCustomersQuery } from '../customers/types';
import type {
  BudgetEngagementOption,
  CreateCustomerBudgetInput,
  CustomerBudget,
  CustomerBudgetActuals,
  ImportBudgetResult,
  PatchCustomerBudgetInput,
} from './types';

type One = { data: CustomerBudget };
type ImportResponse = { data: ImportBudgetResult };

/** Pagination + as-of snapshot for the per-customer budget list. */
export interface BudgetListQuery {
  /** Restrict to budgets in force on this date. Omit for full history. */
  asOf?: string;
  limit?: number;
  offset?: number;
}

function budgetQuery(opts: BudgetListQuery): Record<string, string | number> {
  const query: Record<string, string | number> = {};
  if (opts.asOf) query.asOf = opts.asOf;
  if (opts.limit != null) query.limit = opts.limit;
  if (opts.offset != null) query.offset = opts.offset;
  return query;
}

export class CustomerBudgetsClient extends BaseClient {
  /**
   * A customer's budgets. Pass `asOf` for the ones in force on that date —
   * at most one per engagement, plus one attached to none; omit it for the
   * full history, newest first, which is what the tab's "show past budgets"
   * view reads.
   */
  list(customerId: string, opts: BudgetListQuery = {}): Promise<Paged<CustomerBudget>> {
    return this.request(`/api/customers/${encodeURIComponent(customerId)}/budgets`, {
      query: budgetQuery(opts),
    });
  }

  /**
   * Budget-versus-actual for the most recent period occurrences of the named
   * budgets — what the Budgets tab shows beneath each row. Each budget
   * resolves its OWN reset period, so a monthly and a quarterly budget on one
   * customer report months and quarters respectively.
   *
   * `today` is the caller's local date, not the server's: period windows are
   * calendar-anchored, and a UTC server would put anyone west of Greenwich in
   * yesterday's period for part of every day.
   */
  actuals(
    customerId: string,
    opts: { budgetIds?: readonly string[]; periods?: number; today?: string } = {},
  ): Promise<{ data: CustomerBudgetActuals[] }> {
    // An omitted `budgetIds` means "every budget"; an EMPTY one means "none",
    // and the two must not collapse. Dropping an empty array from the query
    // string would ask the server for all of them — the opposite of what the
    // caller said — so answer it here instead of sending a request whose
    // parameters no longer carry the question.
    if (opts.budgetIds != null && opts.budgetIds.length === 0) {
      return Promise.resolve({ data: [] });
    }
    const query: Record<string, string | number> = {};
    if (opts.budgetIds?.length) query.budgetIds = opts.budgetIds.join(',');
    if (opts.periods != null) query.periods = opts.periods;
    if (opts.today) query.today = opts.today;
    return this.request(`/api/customers/${encodeURIComponent(customerId)}/budgets/actuals`, {
      query,
    });
  }

  /**
   * The engagements this customer's budgets may name — the form picker's
   * source. Served by the budgets surface, not the SOW one, so a caller who
   * can write budgets can always fill the required field even without
   * `customer.sow.view`. Every version's engagement, deduped to the newest;
   * ended ones included, because a budget may be historical.
   */
  engagementOptions(customerId: string): Promise<{ data: BudgetEngagementOption[] }> {
    return this.request(`/api/customers/${encodeURIComponent(customerId)}/budgets/engagements`);
  }

  get(id: string): Promise<One> {
    return this.request(`/api/customer-budgets/${encodeURIComponent(id)}`);
  }

  create(customerId: string, input: CreateCustomerBudgetInput): Promise<One> {
    return this.request(`/api/customers/${encodeURIComponent(customerId)}/budgets`, {
      method: 'POST',
      body: input,
    });
  }

  /** Partial update. `allocations`, when present, REPLACES the stored map. */
  patch(id: string, input: PatchCustomerBudgetInput): Promise<One> {
    return this.request(`/api/customer-budgets/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  /**
   * Hard delete. Use this only to remove a budget that should never have
   * existed — ENDING a budget is a `patch` setting `effectiveTo`, which keeps
   * it in history so past entries still resolve against the allowance that was
   * actually in force.
   */
  delete(id: string): Promise<One> {
    return this.request(`/api/customer-budgets/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /**
   * Download budgets as an Excel (.xlsx) blob in the wide format: one row per
   * budget, with a column per service role. Gated server-side on
   * `customer.budget.view`. `customerFilter` mirrors the customers list filter
   * so the export covers only the customers visible on screen; pass `{}` for
   * all of them.
   */
  exportFile(
    customerFilter: ExportCustomersQuery = {},
  ): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/customer-budgets/export', {
      query: normalizeQuery(customerFilter),
    });
  }

  /** Blank .xlsx template — header row only, one column per active role. */
  exportTemplate(): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/customer-budgets/import/template');
  }

  /**
   * Upload an .xlsx or .csv file. Each row is a COMPLETE budget for the
   * customer and window it names: a row whose date range, roles and hours all
   * match the stored budget is ignored, and any other row creates a new budget,
   * reshaping the ones it overlaps.
   *
   * Unlike the team importer, customers absent from the file are left alone —
   * omitting a budget never deletes it. Best-effort: valid rows persist,
   * invalid rows come back with per-row errors. Pass `idempotencyKey` to make
   * the POST retry-safe.
   */
  importFile(file: File, opts: { idempotencyKey?: string } = {}): Promise<ImportResponse> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers: Record<string, string> = {};
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    return this.requestMultipart('/api/customer-budgets/import', fd, { headers });
  }
}
